import * as types from 'src/store/mutation-types'
import { Notify } from 'quasar'
import { QuoteTextTemplateClient } from 'src/api/QuoteTextTemplates'
import { createErrorFromResponse } from 'src/utils/error'
import i18n from 'src/i18n/i18n'

// initial state
const state = {
  quoteTextTemplates: [],
  quoteTextTemplatesMeta: {
    quote_top: null,
    quote_bottom: null
  },
  quoteTextTemplatesLoaded: false
}

// getters
const getters = {
  quoteTextTemplates: state => state.quoteTextTemplates,
  quoteTextTemplatesMeta: state => state.quoteTextTemplatesMeta,
  quoteTextTemplatesLoaded: state => state.quoteTextTemplatesLoaded
}

const paginationDefault = {
  page: 1,
  rowsPerPage: 15,
  descending: true,
  sortBy: 'created_at',
  additionalIncludes: []
}

const actions = {
  listQuoteTextTemplates ({ commit, state }, { pagination, type } = {}) {
    pagination = { ...paginationDefault, ...(pagination || {}) }

    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']
    commit(types.SET_QUOTE_TEXT_TEMPLATES_LOADED, false)

    const client = filter => new QuoteTextTemplateClient()
      .include(pagination.additionalIncludes)
      .sort(sort)
      .limit(pagination.rowsPerPage)
      .page(pagination.page)
      .filter(filter)
      .list()

    return Promise.all(Object.keys(state.quoteTextTemplatesMeta)
      .map(type => client(['type', '=', type])))
      .then(responses => {
        const templates = [...responses[0].data.data, ...responses[1].data.data]

        const meta = {
          quote_top: responses[0].data.meta,
          quote_bottom: responses[1].data.meta
        }

        commit(types.SET_QUOTE_TEXT_TEMPLATES, templates)
        commit(types.SET_QUOTE_TEXT_TEMPLATES_META, meta)
        commit(types.SET_QUOTE_TEXT_TEMPLATES_LOADED, true)

        return {
          quoteTextTemplates: state.quoteTextTemplates,
          meta: state.quoteTextTemplatesMeta
        }
      })
  },

  fetchNextQuoteTextTemplateChunk ({ commit, state }, { done, pagination = {}, type }) {
    if (!state.quoteTextTemplatesLoaded) {
      return done()
    } else if (!state.quoteTextTemplatesMeta) {
      return done(true)
    }

    if (!type) {
      throw new Error('Type is required')
    }

    pagination = { ...paginationDefault, ...(pagination || {}) }
    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']

    const filter = ['type', '=', type]

    let currentPage = state.quoteTextTemplatesMeta[type].pagination.current_page
    let totalPages = state.quoteTextTemplatesMeta[type].pagination.total_pages

    // Check if there is still a chunk to fetch available.
    if (currentPage < totalPages) {
      let client = new QuoteTextTemplateClient()
        .include(pagination.additionalIncludes)
        .sort(sort)
        .limit(pagination.rowsPerPage)
        .page(currentPage + 1)

      if (filter) {
        client.filter(filter)
      }

      return client
        .list()
        .then(response => {
          const newQuoteTextTemplates = state.quoteTextTemplates.concat(response.data.data)

          let meta = Object.assign({}, state.invoiceTextTemplatesMeta)
          meta[type] = response.data.meta

          commit(types.SET_QUOTE_TEXT_TEMPLATES, newQuoteTextTemplates)
          commit(types.SET_QUOTE_TEXT_TEMPLATES_META, meta)

          // Indicate that data has been loaded successfully.
          done()
        })
    } else {
      // No more data available to fetch.
      // Passing true to the callback function tells the infinite scroll component to stop loading.
      done(true)
    }
  },

  createQuoteTextTemplate ({ commit, state }, quoteTextTemplate) {
    return new QuoteTextTemplateClient()
      .create(quoteTextTemplate)
      .then(response => commit(types.ADD_QUOTE_TEXT_TEMPLATE, response.data.data))
  },

  updateQuoteTextTemplate ({ commit, state }, { template, id }) {
    return new QuoteTextTemplateClient()
      .update(id, template)
      .then(response => commit(types.UPDATE_QUOTE_TEXT_TEMPLATE, response.data.data))
  },

  deleteQuoteTextTemplate ({ commit, state }, quoteTextTemplate) {
    const type = quoteTextTemplate.type

    return new QuoteTextTemplateClient()
      .destroy(quoteTextTemplate.id)
      .then(() => commit(types.REMOVE_QUOTE_TEXT_TEMPLATE, quoteTextTemplate))
      .then(() => {
        const meta = JSON.parse(JSON.stringify(state.quoteTextTemplatesMeta))
        meta[type].pagination = {
          total: state.quoteTextTemplatesMeta[type].pagination.total - 1
        }

        commit(types.SET_QUOTE_TEXT_TEMPLATES_META, meta)
      })
      .then(() => Notify.create({ message: i18n.tc('quote_text_template_deleted'), type: 'positive' }))
      .then(() => Promise.resolve({
        quoteTextTemplates: state.quoteTextTemplates,
        meta: state.quotesMeta
      }))
      .catch(createErrorFromResponse)
  }
}

// mutations
const mutations = {
  [types.SET_QUOTE_TEXT_TEMPLATES] (state, templates) {
    state.quoteTextTemplates = templates
  },

  [types.SET_QUOTE_TEXT_TEMPLATES_META] (state, templateMeta) {
    state.quoteTextTemplatesMeta = templateMeta
  },

  [types.SET_QUOTE_TEXT_TEMPLATES_LOADED] (state, loaded) {
    state.quoteTextTemplatesLoaded = loaded
  },

  [types.UPDATE_QUOTE_TEXT_TEMPLATE] (state, newTemplate) {
    let templateIndex = state.quoteTextTemplates.findIndex(template => template.id === newTemplate.id)
    state.quoteTextTemplates.splice(templateIndex, 1, newTemplate)
  },

  [types.REMOVE_QUOTE_TEXT_TEMPLATE] (state, template) {
    const toDelete = state.quoteTextTemplates.find(stateTemplate => stateTemplate.id === template.id)
    const index = state.quoteTextTemplates.indexOf(toDelete)

    if (index !== -1) {
      state.quoteTextTemplates.splice(index, 1)
    }
  },

  [types.ADD_QUOTE_TEXT_TEMPLATE] (state, template) {
    state.quoteTextTemplates.unshift(template)
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
